import type { IsSubOperator } from './ProductPropertyEditor.helpers'

const isBlank = (v: unknown) => v === undefined || v === null || v === ''

// Per-field validator for PRODUCT.DISCOUNT. Each missing piece of the
// compound value (`productId`, sub-`operator`, `amount`) returns its own
// `ValidatorReturn` tagged with `metadata.field`, which the value-input
// component reads to attach the error to the correct sub-input.
export const validateProductDiscountValue = ({ value }: { value: unknown }) => {
  const partial =
    typeof value === 'object' && value !== null
      ? (value as {
          productId?: unknown
          operator?: unknown
          amount?: unknown
        })
      : {}
  const results = []
  if (isBlank(partial.productId)) {
    results.push({
      title: 'Incomplete condition',
      message: 'Product required',
      metadata: { field: 'productId' as const }
    })
  }
  if (isBlank(partial.operator)) {
    results.push({
      title: 'Incomplete condition',
      message: 'Operator required',
      metadata: { field: 'operator' as const }
    })
  }
  if (isBlank(partial.amount)) {
    results.push({
      title: 'Incomplete condition',
      message: 'Amount required',
      metadata: { field: 'amount' as const }
    })
  }
  return results.length ? results : undefined
}

// Per-field validator factory for the operators that compare one attribute of
// a product's prices — pricing model, billing frequency, billing type. Each
// missing piece is tagged with `metadata.field` so the editor can put the
// message on the right sub-input.
const priceAttributeValidator =
  (params: {
    field: 'pricingModel' | 'billingFrequency' | 'billingType'
    missingMessage: string
    /**
     * The sub-operators this attribute's editor offers. Leave it off when the
     * attribute has only one comparison — there's no choice to get wrong, and
     * the expression doesn't read the stored operator either.
     */
    subOperators?: ReadonlyArray<IsSubOperator>
  }) =>
  ({ value }: { value: unknown }) => {
    const partial =
      typeof value === 'object' && value !== null
        ? (value as Record<string, unknown>)
        : {}
    const results = []
    if (isBlank(partial.productId)) {
      results.push({
        title: 'Incomplete condition',
        message: 'Product required',
        metadata: { field: 'productId' as const }
      })
    }
    const subOperatorNeeded =
      params.subOperators !== undefined &&
      !params.subOperators.includes(partial.operator as IsSubOperator)
    if (subOperatorNeeded) {
      results.push({
        title: 'Incomplete condition',
        message: 'Operator required',
        metadata: { field: 'operator' as const }
      })
    }
    if (isBlank(partial[params.field])) {
      results.push({
        title: 'Incomplete condition',
        message: params.missingMessage,
        metadata: { field: params.field }
      })
    }
    return results.length ? results : undefined
  }

export const validateProductPricingModelValue = priceAttributeValidator({
  field: 'pricingModel',
  missingMessage: 'Pricing model required',
  subOperators: ['IS', 'IS_NOT']
})

export const validateProductBillingFrequencyValue = priceAttributeValidator({
  field: 'billingFrequency',
  missingMessage: 'Billing frequency required',
  subOperators: ['IS', 'IS_NOT']
})

// Billing type offers one comparison, so there's no operator to validate. The
// expression ignores the stored one too, which is what stops a row that picked
// up an "is not" from somewhere evaluating to a flat false.
export const validateProductBillingTypeValue = priceAttributeValidator({
  field: 'billingType',
  missingMessage: 'Billing type required'
})

// Per-field validator for PRODUCT.HAS_DISCOUNT. Requires both a picked
// product and an explicit yes/no answer; each missing field is tagged so
// the editor can route the message to the right sub-input.
export const validateProductHasDiscountValue = ({
  value
}: {
  value: unknown
}) => {
  const partial =
    typeof value === 'object' && value !== null
      ? (value as { productId?: unknown; hasDiscount?: unknown })
      : {}
  const results = []
  if (isBlank(partial.productId)) {
    results.push({
      title: 'Incomplete condition',
      message: 'Product required',
      metadata: { field: 'productId' as const }
    })
  }
  if (typeof partial.hasDiscount !== 'boolean') {
    results.push({
      title: 'Incomplete condition',
      message: 'Yes/No required',
      metadata: { field: 'hasDiscount' as const }
    })
  }
  return results.length ? results : undefined
}
